// apps/web/src/features/bookings/components/InventorySelector.tsx
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { InventorySlotAvailability } from '@lov/shared';
import type { InventorySelection } from '../hooks/useBookingFlow';
import { EQUIPMENT_CATEGORY_CONFIG } from '../utils/equipmentHelpers';
import type { InventoryCategory } from '@lov/shared';

interface InventorySelectorProps {
  inventory: InventorySlotAvailability[];
  selectedInventory: InventorySelection[];
  selectedStartTime: string | null;
  selectedEndTime: string | null;
  onUpdateQuantity: (venueInventoryId: string, quantity: number) => void;
}

const InventoryCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(1.5),
}));

const ItemInfo = styled(Box)({
  flex: 1,
});

const QuantityControl = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const QuantityDisplay = styled(Box)(() => ({
  minWidth: 40,
  textAlign: 'center',
  fontSize: '1.125rem',
  fontWeight: 600,
}));

const PriceTag = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
}));

const AvailabilityTag = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isLow',
})<{ isLow?: boolean }>(({ theme, isLow }) => ({
  fontSize: '0.75rem',
  color: isLow ? theme.palette.warning.main : theme.palette.text.secondary,
}));

function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Calculate available quantity for an inventory item in the selected time range
 */
function getAvailableQuantity(
  item: InventorySlotAvailability,
  selectedStartTime: string | null,
  selectedEndTime: string | null
): number {
  // If no time range is selected, return total quantity
  if (!selectedStartTime || !selectedEndTime) {
    return item.totalQuantity;
  }

  // Find the minimum available quantity across all slots in the selected time range
  const slotsInRange = item.slots.filter((slot) => {
    return slot.startTime >= selectedStartTime && slot.endTime <= selectedEndTime;
  });

  if (slotsInRange.length === 0) {
    return 0;
  }

  // Return the minimum available quantity across all slots
  return Math.min(...slotsInRange.map((slot) => slot.available));
}

export function InventorySelector({
  inventory,
  selectedInventory,
  selectedStartTime,
  selectedEndTime,
  onUpdateQuantity,
}: InventorySelectorProps) {
  const getSelectedQuantity = (inventoryItemId: string): number => {
    const item = selectedInventory.find((i) => i.venueInventoryId === inventoryItemId);
    return item?.quantity || 0;
  };

  const handleIncrement = (item: InventorySlotAvailability) => {
    const currentQty = getSelectedQuantity(item.inventoryItemId);
    const available = getAvailableQuantity(item, selectedStartTime, selectedEndTime);
    if (currentQty < available) {
      onUpdateQuantity(item.inventoryItemId, currentQty + 1);
    }
  };

  const handleDecrement = (item: InventorySlotAvailability) => {
    const currentQty = getSelectedQuantity(item.inventoryItemId);
    if (currentQty > 0) {
      onUpdateQuantity(item.inventoryItemId, currentQty - 1);
    }
  };

  if (inventory.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No equipment available for the selected time slot.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Select Equipment
      </Typography>

      {inventory.map((item) => {
        const quantity = getSelectedQuantity(item.inventoryItemId);
        const available = getAvailableQuantity(item, selectedStartTime, selectedEndTime);
        const isLowAvailability = available <= 2;

        const categoryConfig = EQUIPMENT_CATEGORY_CONFIG[item.category as InventoryCategory];
        const IconComponent = categoryConfig?.icon;

        return (
          <InventoryCard key={item.inventoryItemId}>
            <ItemInfo>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {IconComponent && <IconComponent sx={{ fontSize: 20, color: 'text.secondary' }} />}
                <Typography variant="subtitle1" fontWeight={600}>
                  {item.itemName}
                </Typography>
              </Box>
              <PriceTag variant="body2">
                {formatPrice(
                  item.effectivePricePerHour ?? item.pricePerHour ?? (item.pricePerHalfHour ? item.pricePerHalfHour * 2 : 0)
                )}
                /hr
              </PriceTag>
              {item.tierName && (
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block' }}>
                  {item.tierName}
                </Typography>
              )}
              <AvailabilityTag isLow={isLowAvailability}>
                {available} available
                {isLowAvailability && ' - Low stock!'}
              </AvailabilityTag>
            </ItemInfo>

            <QuantityControl>
              <IconButton
                size="small"
                onClick={() => handleDecrement(item)}
                disabled={quantity === 0}
                aria-label={`Decrease ${item.itemName} quantity`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>

              <QuantityDisplay>{quantity}</QuantityDisplay>

              <IconButton
                size="small"
                onClick={() => handleIncrement(item)}
                disabled={quantity >= available}
                aria-label={`Increase ${item.itemName} quantity`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </QuantityControl>
          </InventoryCard>
        );
      })}
    </Box>
  );
}
